/**
 * 🏪 Auction House — public player-to-player bidding marketplace.
 *
 * Tabs: Browse · Mine · Bidding · Won · Sell
 * Filters by slot/rarity/currency. Real bidding, Buy-It-Now, anti-snipe.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import RotationPoolAdmin from "@/components/RotationPoolAdmin";
import ConsumablesShop from "@/components/ConsumablesShop";
import SellToHouse from "@/components/SellToHouse";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RARITY_BADGE = {
  starter:   "bg-neutral-700/40 border-neutral-600 text-neutral-300",
  common:    "bg-gray-700/40 border-gray-500 text-gray-200",
  uncommon:  "bg-emerald-900/40 border-emerald-700 text-emerald-200",
  rare:      "bg-blue-900/40 border-blue-700 text-blue-200",
  forged:    "bg-amber-900/40 border-amber-700 text-amber-200",
  inferno:   "bg-red-900/40 border-red-700 text-red-200",
  legendary: "bg-purple-900/40 border-purple-700 text-purple-200",
};

const SLOT_ICON = {
  helmet: "🪖",
  chest: "🦺",
  talon: "🥊",
  wings: "🪽",
  aura: "✨",
  consumable: "🧪",
};

function fmtNum(n) {
  return new Intl.NumberFormat().format(Number(n || 0));
}

function fmtCountdown(secs) {
  if (secs == null) return "?";
  if (secs <= 0) return "Ended";
  const d = Math.floor(secs / 86400);
  const h = Math.floor((secs % 86400) / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (d) return `${d}d ${h}h`;
  if (h) return `${h}h ${m}m`;
  if (m) return `${m}m ${s}s`;
  return `${s}s`;
}

function CurrencyChip({ currency, value }) {
  const icon = currency === "chits" ? "🪙"
             : currency === "od" ? "💎"
             : currency === "spirit" ? "🍚"          /* legacy P2P listings */
             : currency === "battle_coins" ? "⚔️"
             : currency === "phoenix" ? "🐦‍🔥"
             : "🪙";
  return (
    <span className="inline-flex items-center gap-1 font-bold">
      <b className="text-amber-200">{fmtNum(value)}</b>
      <span className="text-neutral-400">{icon}</span>
    </span>
  );
}

export default function AuctionHousePage() {
  const { isAuthed, user } = useAuth();
  const [state, setState] = useState(null);
  const [tab, setTab] = useState("browse");
  const [busy, setBusy] = useState(false);
  const [sellPanel, setSellPanel] = useState(false);
  const [filters, setFilters] = useState({ slot: "", rarity: "", currency: "" });

  const refresh = async () => {
    try {
      const r = await axios.get(`${API}/auction-house/state`);
      setState(r.data);
    } catch (e) {
      if (e?.response?.status !== 401) toast.error("Auction House offline.");
    }
  };

  useEffect(() => {
    if (!isAuthed) return;
    refresh();
    // Live-tick the countdowns every second (no API call)
    const tick = setInterval(() => {
      setState((s) =>
        s
          ? {
              ...s,
              active: s.active.map((l) => ({
                ...l,
                seconds_remaining: Math.max(0, (l.seconds_remaining || 0) - 1),
              })),
              mine: s.mine.map((l) => ({
                ...l,
                seconds_remaining: Math.max(0, (l.seconds_remaining || 0) - 1),
              })),
              bidding: s.bidding.map((l) => ({
                ...l,
                seconds_remaining: Math.max(0, (l.seconds_remaining || 0) - 1),
              })),
            }
          : s
      );
    }, 1000);
    // Re-fetch full state every 20s (server settles expirations on read)
    const poll = setInterval(refresh, 20000);
    return () => {
      clearInterval(tick);
      clearInterval(poll);
    };
  }, [isAuthed]);

  const filteredBrowse = useMemo(() => {
    if (!state?.active) return [];
    return state.active.filter((l) => {
      if (filters.slot && l.item_meta?.slot !== filters.slot) return false;
      if (filters.rarity && l.item_meta?.rarity !== filters.rarity) return false;
      if (filters.currency && l.currency !== filters.currency) return false;
      return true;
    });
  }, [state, filters]);

  const _currencyIcon = (cur) => (
    cur === "chits" ? "🪙"
    : cur === "od" ? "💎"
    : cur === "spirit" ? "🍚"           /* legacy listings */
    : cur === "battle_coins" ? "⚔️"
    : cur === "phoenix" ? "🐦‍🔥"
    : "🪙"
  );

  const onBid = async (listing) => {
    const minBid = listing.current_bid > 0 ? listing.min_next_bid : listing.starting_price;
    const raw = window.prompt(
      `Place your bid (min ${fmtNum(minBid)} ${_currencyIcon(listing.currency)}):`,
      String(minBid)
    );
    if (raw == null) return;
    const amt = parseInt(raw, 10);
    if (!Number.isFinite(amt) || amt <= 0) {
      toast.error("Bid must be a positive integer.");
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/auction-house/bid`, {
        listing_id: listing.listing_id,
        amount: amt,
      });
      toast.success(`🔨 Bid placed at ${fmtNum(amt)}`);
      if (r.data?.anti_snipe_extended) {
        toast.info("⏱️ Anti-snipe — auction extended by 5 min");
      }
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bid failed.");
    } finally {
      setBusy(false);
    }
  };

  const onBuyNow = async (listing) => {
    if (!window.confirm(
      `Buy-It-Now: ${listing.item_meta?.name} for ${fmtNum(listing.buy_now_price)} ${_currencyIcon(listing.currency)}?`
    )) return;
    setBusy(true);
    try {
      await axios.post(`${API}/auction-house/buy-now`, { listing_id: listing.listing_id });
      toast.success(`✅ Bought ${listing.item_meta?.name}!`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purchase failed.");
    } finally {
      setBusy(false);
    }
  };

  const onCancel = async (listing) => {
    if (!window.confirm("Cancel this listing? Item + fee will be refunded.")) return;
    setBusy(true);
    try {
      await axios.post(`${API}/auction-house/cancel`, { listing_id: listing.listing_id });
      toast.success("🗑️ Listing cancelled.");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed.");
    } finally {
      setBusy(false);
    }
  };

  if (!isAuthed) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#10001a] via-black to-[#1a0010] text-amber-100">
        <Header />
        <div className="max-w-3xl mx-auto px-4 py-20 text-center">
          <div className="text-6xl mb-4">🏪</div>
          <h1 className="text-4xl font-black mb-2" style={{ fontFamily: "Anton, sans-serif" }}>
            AUCTION HOUSE
          </h1>
          <p className="text-neutral-400">Sign in to bid, sell, and snipe.</p>
        </div>
        <Footer />
      </div>
    );
  }

  const wallet = state?.wallet || {};
  const isOwner = user?.role === "owner";
  const tabs = [
    { id: "browse",  label: `🔨 Browse · ${state?.active?.length || 0}` },
    { id: "house",   label: `🛒 House Catalog` },
    { id: "consumables", label: `🎰 Consumables` },
    { id: "sell-house", label: `🏛️ Sell to House` },
    { id: "bidding", label: `🎯 Bidding · ${state?.bidding?.length || 0}` },
    { id: "mine",    label: `📦 Mine · ${state?.mine?.length || 0}` },
    { id: "won",     label: `🏆 Won` },
    ...(isOwner ? [{ id: "rotation", label: `🎡 Rotation Pool` }] : []),
  ];

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-[#10001a] via-black to-[#1a0010] text-amber-100"
      data-testid="auction-house-page"
    >
      <Header />

      <main className="max-w-7xl mx-auto px-4 py-6 pb-24">
        {/* Hero */}
        <div className="rounded-2xl border border-purple-700/40 bg-gradient-to-br from-[#10001a] via-black/60 to-[#1a0010] p-6 mb-6 relative overflow-hidden">
          <div className="absolute -top-8 -right-8 text-[160px] opacity-10 select-none">🏪</div>
          <div className="relative flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[280px]">
              <div className="text-xs uppercase tracking-[0.4em] text-purple-300/70 mb-1">Kingdom of Dragonious</div>
              <h1 className="text-4xl md:text-5xl font-black text-amber-200" style={{ fontFamily: "Anton, sans-serif" }}>
                🏪 AUCTION HOUSE
              </h1>
              <p className="text-amber-100/80 text-sm mt-2 max-w-2xl">
                Sell your loot. Bid on theirs. Anti-snipe protection, 10% house cut, public to all of Dragonious.
              </p>
              <a
                href="/bazaar"
                data-testid="link-to-aethelgard-bazaar"
                className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-purple-500/50 bg-purple-900/40 px-3 py-1.5 text-xs font-semibold text-purple-100 hover:bg-purple-800/60"
              >
                🏛️ Visit The Aethelgard Bazaar — player-crafted market →
              </a>
            </div>
            <div className="flex gap-2 items-center flex-wrap justify-end">
              <div className="rounded-full bg-black/60 border border-amber-700/40 px-3 py-2" data-testid="ah-chits-balance">
                <b className="text-amber-200">{fmtNum(wallet.chits)}</b> <span className="text-neutral-400">🪙</span>
              </div>
              <div className="rounded-full bg-black/60 border border-amber-700/40 px-3 py-2" data-testid="ah-battle-coins-balance">
                <b className="text-amber-200">{fmtNum(wallet.battle_coins)}</b> <span className="text-neutral-400">⚔️</span>
              </div>
              <div className="rounded-full bg-black/60 border border-amber-700/40 px-3 py-2" data-testid="ah-spirit-balance">
                <b className="text-amber-200">{fmtNum(wallet.brawler_spirit)}</b> <span className="text-neutral-400">🍚</span>
              </div>
              <div className="rounded-full bg-black/60 border border-amber-700/40 px-3 py-2" data-testid="ah-phoenix-balance">
                <b className="text-amber-200">{fmtNum(wallet.phoenix_tokens)}</b> <span className="text-neutral-400">🐦‍🔥</span>
              </div>
              <div className="rounded-full bg-black/60 border border-amber-700/40 px-3 py-2" data-testid="ah-od-balance">
                <b className="text-amber-200">{fmtNum(wallet.omni_dollars)}</b> <span className="text-neutral-400">💎</span>
              </div>
              <button
                onClick={() => setSellPanel(true)}
                data-testid="open-sell-panel-btn"
                className="px-4 py-2 rounded bg-amber-500 hover:bg-amber-400 text-black font-bold text-sm"
              >
                + Sell Item
              </button>
            </div>
          </div>
        </div>

        {/* Tab bar */}
        <div className="flex flex-wrap gap-2 mb-5" data-testid="ah-tabs">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`ah-tab-${t.id}`}
              className={`px-4 py-2 rounded-full text-xs font-bold border transition ${
                tab === t.id
                  ? "bg-amber-500 text-black border-amber-500"
                  : "bg-black/40 text-amber-200 border-purple-700/40 hover:border-amber-500"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Filters (browse only) */}
        {tab === "browse" && (
          <div className="flex flex-wrap gap-2 mb-4">
            <select
              value={filters.slot}
              onChange={(e) => setFilters((f) => ({ ...f, slot: e.target.value }))}
              data-testid="ah-filter-slot"
              className="bg-black/50 border border-purple-700/40 rounded px-3 py-1.5 text-xs text-amber-100"
            >
              <option value="">All slots</option>
              <option value="helmet">🪖 Helmet</option>
              <option value="chest">🦺 Chest</option>
              <option value="talon">🥊 Talon</option>
              <option value="wings">🪽 Wings</option>
              <option value="aura">✨ Aura</option>
              <option value="consumable">🧪 Consumable</option>
            </select>
            <select
              value={filters.rarity}
              onChange={(e) => setFilters((f) => ({ ...f, rarity: e.target.value }))}
              data-testid="ah-filter-rarity"
              className="bg-black/50 border border-purple-700/40 rounded px-3 py-1.5 text-xs text-amber-100"
            >
              <option value="">All rarities</option>
              {["common","uncommon","rare","forged","inferno","legendary"].map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
            <select
              value={filters.currency}
              onChange={(e) => setFilters((f) => ({ ...f, currency: e.target.value }))}
              data-testid="ah-filter-currency"
              className="bg-black/50 border border-purple-700/40 rounded px-3 py-1.5 text-xs text-amber-100"
            >
              <option value="">Any currency</option>
              <option value="chits">🪙 Chits</option>
              <option value="od">💎 OD</option>
            </select>
          </div>
        )}

        {/* Listing grids */}
        {tab === "browse" && (
          <ListingGrid
            listings={filteredBrowse}
            mode="bid"
            currentUserId={state?.wallet ? undefined : undefined /* not used */}
            onBid={onBid}
            onBuyNow={onBuyNow}
            busy={busy}
            emptyLabel="No live auctions match your filters."
          />
        )}
        {tab === "bidding" && (
          <ListingGrid
            listings={state?.bidding || []}
            mode="bidding"
            onBid={onBid}
            onBuyNow={onBuyNow}
            busy={busy}
            emptyLabel="You're not the high bidder anywhere. Yet."
          />
        )}
        {tab === "mine" && (
          <ListingGrid
            listings={state?.mine || []}
            mode="mine"
            onCancel={onCancel}
            busy={busy}
            emptyLabel="No active listings. Hit '+ Sell Item' to post one."
          />
        )}
        {tab === "won" && (
          <ListingGrid
            listings={state?.won || []}
            mode="won"
            emptyLabel="No wins yet. Pull the trigger on a Buy-Now next time."
          />
        )}
        {tab === "house" && <HouseCatalog />}
        {tab === "consumables" && <ConsumablesShop />}
        {tab === "sell-house" && <SellToHouse />}
        {tab === "rotation" && isOwner && <RotationPoolAdmin />}
      </main>

      {sellPanel && (
        <SellPanel
          state={state}
          onClose={() => setSellPanel(false)}
          onListed={() => {
            setSellPanel(false);
            refresh();
          }}
        />
      )}

      <Footer />
    </div>
  );
}


function ListingGrid({ listings, mode, onBid, onBuyNow, onCancel, busy, emptyLabel }) {
  if (!listings.length) {
    return <div className="text-neutral-500 text-sm italic">{emptyLabel}</div>;
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="ah-listings-grid">
      {listings.map((l) => (
        <ListingCard
          key={l.listing_id}
          listing={l}
          mode={mode}
          busy={busy}
          onBid={onBid ? () => onBid(l) : null}
          onBuyNow={onBuyNow ? () => onBuyNow(l) : null}
          onCancel={onCancel ? () => onCancel(l) : null}
        />
      ))}
    </div>
  );
}


function ListingCard({ listing, mode, busy, onBid, onBuyNow, onCancel }) {
  const item = listing.item_meta || {};
  const rarity = item.rarity || "common";
  const ended = mode === "won" || listing.status !== "active";
  const isSnipeWindow = (listing.seconds_remaining || 0) < 300 && (listing.seconds_remaining || 0) > 0;
  const cur = listing.currency === "chits" ? "🪙"
            : listing.currency === "od" ? "💎"
            : listing.currency === "spirit" ? "🍚"     /* legacy listings */
            : listing.currency === "battle_coins" ? "⚔️"
            : listing.currency === "phoenix" ? "🐦‍🔥"
            : "🪙";

  return (
    <div
      data-testid={`ah-listing-${listing.listing_id}`}
      className={`rounded-xl border bg-gradient-to-br from-[#10001a] to-black/60 p-4 transition ${
        ended ? "opacity-70 border-neutral-800" :
        isSnipeWindow ? "border-red-500 shadow-[0_0_20px_rgba(239,68,68,0.25)] animate-pulse" :
        "border-purple-700/40 hover:border-amber-500"
      }`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="text-4xl">{item.icon || SLOT_ICON[item.slot] || "📦"}</div>
        <div className={`px-2 py-0.5 rounded-full text-[10px] font-black border ${RARITY_BADGE[rarity] || RARITY_BADGE.common}`}>
          {rarity}
        </div>
      </div>
      <div className="text-sm font-black text-amber-100 truncate" title={item.name}>{item.name || listing.item_id}</div>
      <div className="text-[10px] text-neutral-500 uppercase tracking-widest mt-0.5">{item.slot}</div>

      {mode !== "won" && listing.status === "active" && (
        <div className="mt-3 text-[11px] text-neutral-500 flex items-center justify-between">
          <span>⏱️ <b className={isSnipeWindow ? "text-red-400" : "text-amber-200"}>{fmtCountdown(listing.seconds_remaining)}</b></span>
          <span>{listing.bid_count} {listing.bid_count === 1 ? "bid" : "bids"}</span>
        </div>
      )}

      <div className="mt-3 space-y-1.5">
        {listing.current_bid > 0 ? (
          <div className="text-xs">
            <div className="text-neutral-400 text-[10px] uppercase tracking-widest">Current bid</div>
            <div>
              <CurrencyChip currency={listing.currency} value={listing.current_bid} />
              <span className="text-[10px] text-neutral-500 ml-2">by {listing.current_bid_name || "?"}</span>
            </div>
          </div>
        ) : (
          <div className="text-xs">
            <div className="text-neutral-400 text-[10px] uppercase tracking-widest">Starting price</div>
            <CurrencyChip currency={listing.currency} value={listing.starting_price} />
          </div>
        )}
        {listing.buy_now_price && mode !== "won" && (
          <div className="text-xs">
            <div className="text-neutral-400 text-[10px] uppercase tracking-widest">Buy-It-Now</div>
            <CurrencyChip currency={listing.currency} value={listing.buy_now_price} />
          </div>
        )}
      </div>

      <div className="text-[10px] text-neutral-500 italic mt-3 flex items-center gap-2 flex-wrap">
        <span>Seller: <span className="text-amber-200/80">{listing.seller_name || "?"}</span></span>
        {listing.is_house_rotation && (
          <span
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-amber-500/15 border border-amber-400/40 text-amber-200 uppercase tracking-widest"
            title="House Rotation — auto-listed by The Auctioneer"
            data-testid={`house-rotation-badge-${listing.listing_id}`}
          >
            🎡 Revolving
          </span>
        )}
      </div>

      {/* Action row */}
      <div className="mt-3 flex gap-2">
        {mode === "bid" && (
          <>
            <button
              onClick={onBid}
              disabled={busy}
              data-testid={`bid-btn-${listing.listing_id}`}
              className="flex-1 px-3 py-1.5 rounded bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white font-bold text-[11px]"
            >
              🔨 Bid {fmtNum(listing.current_bid > 0 ? listing.min_next_bid : listing.starting_price)}{cur}
            </button>
            {listing.buy_now_price && (
              <button
                onClick={onBuyNow}
                disabled={busy}
                data-testid={`buynow-btn-${listing.listing_id}`}
                className="px-3 py-1.5 rounded bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black font-bold text-[11px]"
              >
                💰 {fmtNum(listing.buy_now_price)}
              </button>
            )}
          </>
        )}
        {mode === "bidding" && (
          <button
            onClick={onBid}
            disabled={busy}
            className="flex-1 px-3 py-1.5 rounded bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white font-bold text-[11px]"
          >
            🔨 Raise to {fmtNum(listing.min_next_bid)}{cur}
          </button>
        )}
        {mode === "mine" && (
          <>
            {listing.bid_count === 0 ? (
              <button
                onClick={onCancel}
                disabled={busy}
                data-testid={`cancel-btn-${listing.listing_id}`}
                className="flex-1 px-3 py-1.5 rounded bg-red-700 hover:bg-red-600 disabled:opacity-50 text-white font-bold text-[11px]"
              >
                🗑️ Cancel
              </button>
            ) : (
              <div className="flex-1 text-[10px] text-neutral-500 italic text-center py-2">
                Locked — has bids
              </div>
            )}
          </>
        )}
        {mode === "won" && (
          <div className="flex-1 text-[10px] text-amber-400 font-bold text-center py-2">
            🏆 Sold for {fmtNum(listing.final_price)} {cur}
          </div>
        )}
      </div>
    </div>
  );
}


function SellPanel({ state, onClose, onListed }) {
  const tradeable = state?.tradeable_inventory || [];
  const constants = state?.constants || {};
  const [selected, setSelected] = useState(tradeable[0]?.item_id || "");
  const [currency, setCurrency] = useState("chits");
  const [startingPrice, setStartingPrice] = useState(100);
  const [buyNow, setBuyNow] = useState("");
  const [duration, setDuration] = useState(48);
  const [busy, setBusy] = useState(false);

  const fee = constants.duration_fees?.[duration] || 10;

  const submit = async () => {
    if (!selected) { toast.error("Pick an item."); return; }
    setBusy(true);
    try {
      const payload = {
        item_id: selected,
        currency,
        starting_price: parseInt(startingPrice, 10),
        duration_hours: duration,
      };
      const bn = parseInt(buyNow, 10);
      if (Number.isFinite(bn) && bn > 0) payload.buy_now_price = bn;
      await axios.post(`${API}/auction-house/list`, payload);
      toast.success("✅ Listed!");
      onListed();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Listing failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4" data-testid="sell-panel">
      <div className="relative w-full max-w-md rounded-2xl border border-purple-700/50 bg-gradient-to-br from-[#1a0010] via-black to-[#10001a] p-6">
        <button
          onClick={onClose}
          data-testid="sell-panel-close"
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/60 hover:bg-amber-500/30 flex items-center justify-center text-amber-300 text-lg"
        >×</button>
        <h2 className="text-2xl font-black text-amber-200 mb-1" style={{ fontFamily: "Anton, sans-serif" }}>
          + LIST ITEM
        </h2>
        <p className="text-[11px] text-neutral-500 mb-4">
          Listing fee scales with duration. 10% house cut on sale. Anti-snipe extends auctions by 5 min when bids land in the final 5.
        </p>

        {tradeable.length === 0 ? (
          <div className="text-sm text-neutral-400 italic">You have no tradeable gear right now.</div>
        ) : (
          <div className="space-y-3">
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-neutral-400">Item</span>
              <select
                value={selected}
                onChange={(e) => setSelected(e.target.value)}
                data-testid="sell-item-select"
                className="w-full bg-black/50 border border-purple-700/40 rounded px-3 py-2 text-sm text-amber-100"
              >
                {tradeable.map((g) => (
                  <option key={g.item_id} value={g.item_id}>
                    {g.icon || ""} {g.name} (x{g.qty})
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-neutral-400">Currency</span>
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                data-testid="sell-currency-select"
                className="w-full bg-black/50 border border-purple-700/40 rounded px-3 py-2 text-sm text-amber-100"
              >
                <option value="chits">🪙 Chits</option>
                <option value="od">💎 Omni Dollars</option>
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-neutral-400">Starting price</span>
              <input
                type="number"
                value={startingPrice}
                onChange={(e) => setStartingPrice(e.target.value)}
                data-testid="sell-starting-price"
                className="w-full bg-black/50 border border-purple-700/40 rounded px-3 py-2 text-sm text-amber-100"
              />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-neutral-400">
                Buy-It-Now (optional)
              </span>
              <input
                type="number"
                value={buyNow}
                onChange={(e) => setBuyNow(e.target.value)}
                placeholder="Leave blank for pure auction"
                data-testid="sell-buy-now-price"
                className="w-full bg-black/50 border border-purple-700/40 rounded px-3 py-2 text-sm text-amber-100"
              />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-neutral-400">Duration</span>
              <select
                value={duration}
                onChange={(e) => setDuration(parseInt(e.target.value, 10))}
                data-testid="sell-duration-select"
                className="w-full bg-black/50 border border-purple-700/40 rounded px-3 py-2 text-sm text-amber-100"
              >
                <option value={24}>24h — 5 🍚 fee</option>
                <option value={48}>48h — 10 🍚 fee</option>
                <option value={168}>7 days — 25 🍚 fee</option>
              </select>
            </label>

            <div className="text-[11px] text-neutral-500 italic">
              Listing fee: <b className="text-amber-300">{fee} 🍚</b> (refunded if cancelled/sold).
            </div>

            <button
              onClick={submit}
              disabled={busy}
              data-testid="sell-submit-btn"
              className="w-full px-4 py-2 rounded bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black font-black text-sm"
            >
              {busy ? "Posting..." : "Post Auction"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════
// 🛒 House Catalog — fixed-price gear sets (1500 Chits per piece)
// ═══════════════════════════════════════════════════════════════════════
function HouseCatalog() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/auction/house-catalog`);
      setState(r.data);
    } catch {
      toast.error("Failed to load House Catalog.");
    }
  };
  useEffect(() => { load(); }, []);

  const buy = async (item_id, name, icon) => {
    setBusy(item_id);
    try {
      const r = await axios.post(`${API}/auction/house-buy`, { item_id });
      toast.success(
        `${icon || "🛡️"} +1 ${name} (tradeable!) · ${Number(r.data.chits).toLocaleString()} 🪙 left`,
        { duration: 4500 },
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't buy that piece.");
    } finally {
      setBusy(null);
    }
  };

  if (!state) {
    return (
      <div className="text-center py-12 text-amber-300 animate-pulse" data-testid="house-catalog-loading">
        🛒 Loading House Catalog...
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="house-catalog-root">
      {/* Header band */}
      <div className="rounded-xl border-2 border-amber-500/40 bg-gradient-to-br from-amber-950/30 via-black/50 to-purple-950/30 p-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.4em] text-amber-300/80">House Vendor · Fixed Price</div>
          <h3 className="text-xl font-black text-amber-200" style={{ fontFamily: "Anton, sans-serif" }}>
            🛒 GEAR SET CATALOG
          </h3>
          <p className="text-[11px] text-neutral-400 mt-1 max-w-xl">
            Each piece is priced individually. Anyone can buy — class-locks apply at equip time only.
            House-bought pieces are <b className="text-emerald-300">tradeable</b>, so flip them on the Browse tab if you&apos;ve got a buyer.
          </p>
        </div>
        <div className="rounded-full bg-black/60 border border-amber-700/40 px-3 py-2 text-sm" data-testid="house-chits-balance">
          <b className="text-amber-200">{Number(state.chits).toLocaleString()}</b> 🪙
        </div>
      </div>

      {/* Sets list */}
      {(state.sets || []).map((set) => (
        <SetBlock key={set.set_id} set={set} userClass={state.user_class}
                  busy={busy} onBuy={buy} />
      ))}

      {/* 🪶 Standalone items — owner-locked Feb 2026 anti-bundling directive.
          These render as discrete cards OUTSIDE the set grid. No set header,
          no set name, no implied synergy. Each card carries its own price. */}
      {(state.standalone_items || []).length > 0 && (
        <div className="rounded-xl border border-amber-600/40 bg-black/40 p-4" data-testid="house-standalone-section">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xl">🪶</span>
            <div>
              <h4 className="text-lg font-black text-amber-100" style={{ fontFamily: "Anton, sans-serif" }}>
                INDIVIDUAL PIECES
              </h4>
              <p className="text-[10px] uppercase tracking-widest text-neutral-500">
                {state.standalone_items.length} item{state.standalone_items.length === 1 ? "" : "s"} · Sold one-at-a-time
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {state.standalone_items.map((p) => (
              <StandalonePieceCard
                key={p.item_id}
                p={p}
                busy={busy}
                onBuy={buy}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StandalonePieceCard({ p, busy, onBuy }) {
  const owned = (p.owned || 0) > 0;
  const canAfford = !!p.affordable;
  const isBusy = busy === p.item_id;
  const cIcon = p.currency_icon || "🪙";
  const price = (p.price ?? p.price_chits ?? 0).toLocaleString();
  return (
    <div
      data-testid={`house-piece-${p.item_id}`}
      className="rounded-lg border border-neutral-800 bg-black/50 p-3 flex items-start gap-2"
    >
      <div className="text-2xl">{p.icon || "🪶"}</div>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-sm text-amber-100 truncate">{p.name}</div>
        <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1">{p.slot}</div>
        {p.stats && Object.keys(p.stats).length > 0 && (
          <div className="text-[10px] text-emerald-300/80 mb-1">
            {Object.entries(p.stats).map(([k, v]) => `+${v} ${k.toUpperCase()}`).join(" · ")}
          </div>
        )}
        {p.unique_effect?.name && (
          <div className="text-[10px] text-purple-300 italic mb-1" data-testid={`house-piece-effect-${p.item_id}`}>
            ✨ {p.unique_effect.name}
          </div>
        )}
        <div className="flex items-center justify-between mt-1.5">
          <span className="text-xs text-amber-300 font-black" data-testid={`house-piece-price-${p.item_id}`}>
            {price} {cIcon}
          </span>
          {owned ? (
            <span data-testid={`house-piece-owned-${p.item_id}`}
                  className="text-[10px] uppercase tracking-wider font-black px-2 py-1 rounded bg-emerald-900/40 border border-emerald-700/40 text-emerald-300">
              ✓ Own ×{p.owned}
            </span>
          ) : !canAfford ? (
            <button disabled
                    data-testid={`house-piece-poor-${p.item_id}`}
                    className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded bg-yellow-900/30 border border-yellow-700/40 text-yellow-300 cursor-not-allowed">
              Need {cIcon}
            </button>
          ) : (
            <button disabled={isBusy}
                    onClick={() => onBuy(p.item_id, p.name, p.icon)}
                    data-testid={`house-piece-buy-${p.item_id}`}
                    className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded bg-amber-500 hover:bg-amber-400 text-black disabled:bg-neutral-800 disabled:text-neutral-500 transition">
              {isBusy ? "Buying..." : "🛒 Buy"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


function SetBlock({ set, userClass, busy, onBuy }) {
  const isComingSoon = set.status === "coming_soon";
  return (
    <div
      data-testid={`house-set-${set.set_id}`}
      className={`rounded-xl border p-4 ${
        isComingSoon
          ? "border-neutral-700/60 bg-black/30 opacity-75"
          : "border-amber-600/40 bg-black/40"
      }`}
    >
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <div className="text-3xl">{set.icon}</div>
        <div className="flex-1 min-w-0">
          <h4 className="text-lg font-black text-amber-100" style={{ fontFamily: "Anton, sans-serif" }}>
            {set.name}
          </h4>
          <div className="flex items-center gap-2 flex-wrap text-[10px] mt-0.5">
            {set.class_lock && (
              <span className={`px-1.5 py-0.5 rounded font-bold uppercase tracking-wider ${
                set.class_lock === userClass
                  ? "bg-emerald-900/40 border border-emerald-700/40 text-emerald-300"
                  : "bg-rose-900/30 border border-rose-700/40 text-rose-300"
              }`}>
                🔒 {set.class_lock}{set.class_lock === userClass ? " (you)" : ""}
              </span>
            )}
            {set.level_req && (
              <span className="px-1.5 py-0.5 rounded font-bold uppercase tracking-wider bg-purple-900/30 border border-purple-700/40 text-purple-200">
                Lv {set.level_req}+
              </span>
            )}
            <span className="px-1.5 py-0.5 rounded uppercase tracking-wider bg-neutral-900 border border-neutral-700 text-neutral-400">
              {set.total_pieces} pieces
            </span>
          </div>
        </div>
        {isComingSoon && (
          <span className="px-2 py-1 rounded-full bg-yellow-800/40 border border-yellow-600/40 text-yellow-200 text-[10px] font-black uppercase tracking-wider"
                data-testid={`house-set-coming-soon-${set.set_id}`}>
            🚧 Coming Soon
          </span>
        )}
      </div>

      {/* Stub set — just description, no buyable pieces */}
      {isComingSoon && (
        <div className="text-xs italic text-neutral-400">
          {set.owner_notes || "Set spec pending — locks soon."}
        </div>
      )}

      {/* Live set — buyable pieces */}
      {!isComingSoon && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {(set.pieces || []).map((p) => {
            const owned = (p.owned || 0) > 0;
            const canAfford = !!p.affordable;
            const isBusy = busy === p.item_id;
            const cIcon = p.currency_icon || "🪙";
            const price = (p.price ?? p.price_chits ?? 0).toLocaleString();
            return (
              <div key={p.item_id}
                   data-testid={`house-piece-${p.item_id}`}
                   className="rounded-lg border border-neutral-800 bg-black/50 p-3 flex items-start gap-2">
                <div className="text-2xl">{p.icon || "🛡️"}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm text-amber-100 truncate">{p.name}</div>
                  <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1">{p.slot}</div>
                  {p.stats && Object.keys(p.stats).length > 0 && (
                    <div className="text-[10px] text-emerald-300/80 mb-1">
                      {Object.entries(p.stats).map(([k, v]) => `+${v} ${k.toUpperCase()}`).join(" · ")}
                    </div>
                  )}
                  {p.class_lock_warning && (
                    <div className="text-[9px] text-rose-400 italic mb-1">
                      ⚠ Won&apos;t equip — wrong class
                    </div>
                  )}
                  <div className="flex items-center justify-between mt-1.5">
                    <span className="text-xs text-amber-300 font-black" data-testid={`house-piece-price-${p.item_id}`}>
                      {price} {cIcon}
                    </span>
                    {owned ? (
                      <span data-testid={`house-piece-owned-${p.item_id}`}
                            className="text-[10px] uppercase tracking-wider font-black px-2 py-1 rounded bg-emerald-900/40 border border-emerald-700/40 text-emerald-300">
                        ✓ Own ×{p.owned}
                      </span>
                    ) : !canAfford ? (
                      <button
                        disabled
                        data-testid={`house-piece-poor-${p.item_id}`}
                        className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded bg-yellow-900/30 border border-yellow-700/40 text-yellow-300 cursor-not-allowed"
                      >
                        Need {cIcon}
                      </button>
                    ) : (
                      <button
                        disabled={isBusy}
                        onClick={() => onBuy(p.item_id, p.name, p.icon)}
                        data-testid={`house-piece-buy-${p.item_id}`}
                        className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded bg-amber-500 hover:bg-amber-400 text-black disabled:bg-neutral-800 disabled:text-neutral-500 transition"
                      >
                        {isBusy ? "Buying..." : "🛒 Buy"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
